import React, { useRef, useState, useEffect, useCallback } from "react";

import styles from "styles.module.css";
import Filter from "./Filter";
import "./styles.css";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import OverLayLoading from "ui/Loading";
import GridCustomPage from "ui/grid/GridPage";
import { useTranslation } from "react-i18next";
import { GridCellProps } from "@progress/kendo-react-grid";
import { PageTypePath } from "utils/pages";
import { ICON_NAMES } from "utils/constant";
import { ContractGridColumn } from "./GridColumns";
import {
	contractConfirmationApi,
	ContractFilter,
	getContractsApi,
} from "api/contract";
import { Contract } from "interface/contract";
import { useInfoStore } from "store/error";
import ContractsMobileList from "./ContractsMobileList";

import { Loading } from "ui/Loading";
import { usePageAccess } from "hooks/usePageAccess";
import { IconButtonActionType } from "interface";

const pageSize = 60;

const Contracts = () => {
	const { t } = useTranslation();

	const overlayRef = useRef<HTMLDivElement>(null);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);

	const gridRef = useRef<HTMLDivElement>(null);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [mobileData, setMobileData] = useState<Contract[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	const [filterOptions, setFilterOptions] = useState<ContractFilter>({
		take: pageSize,
		skip: 0,
		//fromDate: addMonths(new Date(), -36),
		fromDate: new Date(2000, 0, 1),
		toDate: new Date(),
	});

	const [confirmed, setConfirmed] = useState(false);
	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const { setError } = useInfoStore();

	const { pageAccess } = usePageAccess();

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const openPdf = (prop: GridCellProps) => {
		window.open(
			`/${PageTypePath.Pdf}/${prop.dataItem.reportGuid}`,
			"_blank"
		);
		return;
	};

	const openPdfByItem = (item: Contract) => {
		window.open(`/${PageTypePath.Pdf}/${item.reportGuid}`, "_blank");
	};

	const contractConfirmation = (prop: GridCellProps) => {
		const _confirmSign = 1; // Todo: this should updated to field in the porps  dataItem

		contractConfirmationApi({
			confirmSign: _confirmSign,
			contractId: prop.dataItem.id,
		})
			.then((response) => {
				setConfirmed((prev) => !prev);
				console.log();
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const contractConfirmationByItem = (item: Contract) => {
		return contractConfirmationApi({
			confirmSign: 1,
			contractId: item.id,
		})
			.then(() => {
				setConfirmed((prev) => !prev);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
				throw err;
			});
	};

	const requestMobileData = useCallback((skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getContractsApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = (response?.data || []) as Contract[];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setMobileData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setMobileData(incoming);
				} else {
					setMobileData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			});
	}, [filterOptions]);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [confirmed, isMobileView, requestMobileData]);

	if (!pageAccess) {
		return <Loading show={true} />;
	}

	return (
		<div className={`${styles.PageContainer} contract-page`}>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>{t("SalesContract")}</legend>
			</div>

			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{isMobileView ? (
					<ContractsMobileList
						items={mobileData}
						loading={mobileLoading}
						onConfirm={contractConfirmationByItem}
						onOpenPdf={openPdfByItem}
						onLoadMore={() => requestMobileData(mobileNextSkip.current, pageSize)}
					/>
				) : (
					<GridCustomPage
						dataKey="id"
						gridColumns={ContractGridColumn()}
						filter={filterOptions}
						forceUpdate={confirmed}
						exportGridAsPDFRef={pdfRef}
						lastColWidth="140px"
						lastColBtns={[
							{
								iconName: ICON_NAMES.Confirmation,
								showConditionValue: 1,
								conditionField: "showSign",
								onclick: contractConfirmation,
								actionType: IconButtonActionType.Update,
							},
							{
								iconName: ICON_NAMES.Download,
								alwaysShow: true,
								onclick: openPdf,
							},
						]}
						pageAccess={pageAccess}
						exportGridAsExcelRef={excelRef}
						apiMethod={getContractsApi}
						showDetailIcon={false}
					/>
				)}
			</div>
		</div>
	);
};

export default Contracts;
